// Every call the console makes, as TanStack Query options and mutations. Read routes are typed
// by the OpenAPI document; accounts routes by types.ts.
import {
  infiniteQueryOptions,
  queryOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { flash } from '../lib/flash'
import { clearSignupHandoff, signupHandoff } from '../lib/signupHandoff'
import { readSandbox, sandboxFor } from '../sandbox'
import { ApiError, api, isApiError, noContent, notConfigured, typed, unwrap } from './client'
import type {
  AlertEventsPage,
  AlertPreset,
  App,
  AppStart,
  AppUserDevice,
  AuditEntry,
  BrazilAddress,
  CohortComparison,
  Dashboard,
  DashboardPanel,
  DeviceRequest,
  EndpointDetail,
  Endpoints,
  Exits,
  ExitsDaily,
  ExploreFields,
  ExploreLogFacets,
  ExploreLogFacetsQuery,
  ExploreLogs,
  ExploreLogsQuery,
  ExploreMetrics,
  ExploreMetricsCatalog,
  ExploreMetricsQuery,
  ExploreTraces,
  ExploreTracesQuery,
  ExportSignal,
  FeatureFlags,
  Frames,
  Funnel,
  Hangs,
  InAppKind,
  InstrumentationInput,
  IssueCounts,
  IssueDetail,
  IssueFlags,
  IssueHandoff,
  IssueKind,
  IssueMergeResult,
  IssueSeries,
  IssueState,
  IssueStateChange,
  IssueStateFilter,
  IssueSummary,
  Journeys,
  LayerInput,
  MemoryDaily,
  MetricKitHistograms,
  MissingSymbol,
  PanelInput,
  PanelPatch,
  PerformanceSeries,
  Platform,
  PresetPanelResult,
  Project,
  QueryResult,
  QuerySchema,
  ReleaseBreakdown,
  ReleaseComparison,
  ReleaseHealth,
  ReleaseHealthDay,
  ReleaseVitals,
  ReplaySegment,
  Resources,
  ResourcesBattery,
  ResourcesThermal,
  Role,
  RuleStats,
  RuleStatsList,
  ScreenDetail,
  ScreenSummary,
  Screens,
  SeriesMetric,
  SessionCounts,
  SessionFacets,
  SessionListItem,
  SessionResources,
  SessionTimeline,
  Trace,
} from './types'

const DAY = 86_400_000

/** A time window ending now, as the read routes take it (RFC 3339). */
export function lastDays(days: number, now = Date.now()): { from: string; to: string } {
  return { from: new Date(now - days * DAY).toISOString(), to: new Date(now).toISOString() }
}

// Accounts

export const meQuery = queryOptions({
  queryKey: ['me'],
  queryFn: async () => typed(api.GET('/me')),
  staleTime: 60_000,
  retry: false,
})

export const orgQuery = (orgId: string) =>
  queryOptions({
    queryKey: ['org', orgId],
    queryFn: () => typed(api.GET('/orgs/{orgId}', { params: { path: { orgId } } })),
    staleTime: 60_000,
  })

/** This period's usage; refreshed every few minutes, like the metering job. */
export const usageQuery = (orgId: string) =>
  queryOptions({
    queryKey: ['org', orgId, 'usage'],
    queryFn: () => typed(api.GET('/orgs/{orgId}/usage', { params: { path: { orgId } } })),
    staleTime: 300_000,
    retry: false,
  })

export const projectsQuery = (orgId: string) =>
  queryOptions({
    queryKey: ['org', orgId, 'projects'],
    queryFn: async (): Promise<Project[]> => {
      // The sandbox's organization has one project, known from its token (sandbox.ts).
      const sandbox = readSandbox()
      if (sandbox?.orgId === orgId)
        return [
          {
            id: sandbox.projectId,
            org_id: orgId,
            name: sandbox.projectName,
            created_at: '',
            apm_trace_url_template: null,
          },
        ]
      return (await typed(api.GET('/orgs/{orgId}/projects', { params: { path: { orgId } } })))
        .projects
    },
    staleTime: 60_000,
  })

export const appsQuery = (orgId: string, projectId: string) =>
  queryOptions({
    queryKey: ['org', orgId, 'project', projectId, 'apps'],
    queryFn: async (): Promise<App[]> => {
      // The sandbox's apps come with its project; their keys are not shown.
      const sandbox = sandboxFor(projectId)
      if (sandbox)
        return sandbox.apps.map((a) => ({
          ...a,
          org_id: orgId,
          project_id: projectId,
          enabled: true,
          created_at: '',
          keys: [],
        }))
      return (
        await typed(
          api.GET('/orgs/{orgId}/projects/{projectId}/apps', {
            params: { path: { orgId, projectId } },
          }),
        )
      ).apps
    },
    // Like the projects: without it, every component that reads the apps after the first
    // answer fetched them again (twice per Performance or Journeys page load).
    staleTime: 60_000,
  })

export const membersQuery = (orgId: string) =>
  queryOptions({
    queryKey: ['org', orgId, 'members'],
    queryFn: async () =>
      (await typed(api.GET('/orgs/{orgId}/members', { params: { path: { orgId } } }))).members,
  })

export const invitationsQuery = (orgId: string) =>
  queryOptions({
    queryKey: ['org', orgId, 'invitations'],
    queryFn: async () =>
      (await typed(api.GET('/orgs/{orgId}/invitations', { params: { path: { orgId } } })))
        .invitations,
  })

/** The plans (D107: Free, Pro, Enterprise), what each includes, and what Pro costs. */
export const plansQuery = queryOptions({
  queryKey: ['plans'],
  queryFn: () => typed(api.GET('/plans')),
  staleTime: 60 * 60_000,
})

/** A page of the organization's audit log, newest first (owners and admins). */
export async function fetchAudit(
  orgId: string,
  before?: number,
  filter: { kind?: 'agent'; projectId?: string } = {},
): Promise<AuditEntry[]> {
  return (
    await typed(
      api.GET('/orgs/{orgId}/audit', {
        params: {
          path: { orgId },
          query: {
            limit: 50,
            ...(before ? { before } : {}),
            ...(filter.kind ? { kind: filter.kind } : {}),
            ...(filter.projectId ? { project_id: filter.projectId } : {}),
          },
        },
      }),
    )
  ).entries
}

/** What agents did in a project over the last `days` UTC days, by day, tool and client (members). */
export const agentSummaryQuery = (orgId: string, projectId: string, days: number) =>
  queryOptions({
    queryKey: ['org', orgId, 'audit', 'agent-summary', projectId, days],
    queryFn: () =>
      typed(
        api.GET('/orgs/{orgId}/audit/agent-summary', {
          params: { path: { orgId }, query: { project_id: projectId, days } },
        }),
      ),
  })

// Read API (D72)

/**
 * One device model and one exact OS version, as every view's read routes take them (APS-228), and
 * which builds (APS-508): release ones unless `debug` or `all`.
 */
export interface DeviceParams {
  device_model?: string | undefined
  os_version?: string | undefined
  build?: 'debug' | 'all' | undefined
}

/** The device and build filters a request sends: only those that are set. */
const deviceQuery = (d: DeviceParams | undefined) => ({
  ...(d?.device_model ? { device_model: d.device_model } : {}),
  ...(d?.os_version ? { os_version: d.os_version } : {}),
  ...(d?.build ? { build: d.build } : {}),
})

export interface ReleasesParams extends DeviceParams {
  app_id?: string | undefined
  release?: string | undefined
  from: string
  to: string
}

/** Every page of release health for a window (rarely more than one). */
export async function fetchReleases(
  projectId: string,
  q: ReleasesParams,
): Promise<ReleaseHealth[]> {
  const out: ReleaseHealth[] = []
  let cursor: string | undefined
  do {
    const page = unwrap(
      await api.GET('/projects/{projectId}/releases', {
        params: {
          path: { projectId },
          query: {
            from: q.from,
            to: q.to,
            limit: 200,
            ...(q.app_id ? { app_id: q.app_id } : {}),
            ...(q.release !== undefined ? { release: q.release } : {}),
            ...deviceQuery(q),
            ...(cursor ? { cursor } : {}),
          },
        },
      }),
    )
    out.push(...page.releases)
    cursor = page.next_cursor ?? undefined
  } while (cursor && out.length < 2000)
  return out
}

export const releasesQuery = (projectId: string, q: ReleasesParams, key: unknown = q) =>
  queryOptions({
    queryKey: ['project', projectId, 'releases', key],
    queryFn: () => fetchReleases(projectId, q),
    staleTime: 30_000,
  })

/**
 * The project's releases over the last `days` days to `now` (rounded by the caller, to the
 * minute or the hour): the window the shell and the views share. Each new minute is a new key;
 * the list read for the previous one stays while it is read again, so nothing that hangs on the
 * newest release (the "new" view, its counts, the glyphs) flips for a moment and moves the page
 * under a click (APS-452, as APS-348). Only the same project's same window carries over.
 */
export const windowReleasesQuery = (projectId: string, days: number, now: number) =>
  queryOptions({
    ...releasesQuery(projectId, lastDays(days, now), ['window', days, now]),
    placeholderData: (previous, previousQuery) => {
      const key = previousQuery?.queryKey
      const window = key?.[3] as unknown[] | undefined
      return key?.[1] === projectId && window?.[0] === 'window' && window[1] === days
        ? previous
        : undefined
    },
  })

export interface IssuesParams extends DeviceParams {
  app_id?: string | undefined
  release?: string | undefined
  kind?: IssueKind | undefined
  /** Only occurrences in sessions where this feature flag (variant) was evaluated (step 3.6). */
  flag?: string | undefined
  flag_variant?: string | undefined
  sort?: IssueSort | undefined
  order?: 'desc' | 'asc' | undefined
  /** Only occurrences whose last screen load before them was this screen (APS-304). */
  screen?: string | undefined
  /** Only occurrences within 60 s after a failed request to this endpoint (APS-304). */
  endpoint?: EndpointKey | undefined
  /** Which states (D100); the API lists unresolved issues when it is not sent. */
  state?: IssueStateFilter | undefined
  /** Assigned to the person asking, to nobody, or to a member (APS-299). */
  assignee?: 'me' | 'none' | undefined
  /** Only issues first seen in this release (APS-299). */
  new_in?: string | undefined
  /** Each issue's occurrences a day for the sparkline (APS-298). */
  daily?: boolean | undefined
  /** Only occurrences in this app user's sessions: the user.hash the app sends (D114). */
  user?: string | undefined
  days: number
  limit?: number
}

/** What the list sorts by (APS-299). */
export type IssueSort =
  | 'last_seen'
  | 'occurrences'
  | 'users'
  | 'sessions'
  | 'first_seen'
  | 'status'
  | 'kind'

export interface IssuesPage {
  issues: IssueSummary[]
  next_cursor: string | null
}

export async function fetchIssues(
  projectId: string,
  q: IssuesParams,
  cursor?: string,
): Promise<IssuesPage> {
  const range = lastDays(q.days)
  return unwrap(
    await api.GET('/projects/{projectId}/issues', {
      params: {
        path: { projectId },
        query: {
          ...range,
          limit: q.limit ?? 50,
          sort: q.sort ?? 'users',
          ...(q.order ? { order: q.order } : {}),
          ...(q.assignee ? { assignee: q.assignee } : {}),
          ...(q.new_in ? { new_in: q.new_in } : {}),
          ...(q.daily ? { daily: 'true' as const } : {}),
          ...(q.app_id ? { app_id: q.app_id } : {}),
          ...(q.release !== undefined ? { release: q.release } : {}),
          ...deviceQuery(q),
          ...(q.kind ? { kind: q.kind } : {}),
          ...(q.state ? { state: q.state } : {}),
          ...(q.flag ? { flag: q.flag } : {}),
          ...(q.flag && q.flag_variant !== undefined ? { flag_variant: q.flag_variant } : {}),
          ...(q.screen ? { screen: q.screen } : {}),
          ...(q.endpoint
            ? {
                endpoint_method: q.endpoint.method,
                endpoint_host: q.endpoint.host,
                endpoint_template: q.endpoint.template,
              }
            : {}),
          ...(q.user ? { user: q.user } : {}),
          ...(cursor ? { cursor } : {}),
        },
      },
    }),
  )
}

export const issuesQuery = (projectId: string, q: IssuesParams) =>
  queryOptions({
    queryKey: ['project', projectId, 'issues', q],
    queryFn: () => fetchIssues(projectId, q),
    staleTime: 30_000,
  })

// Issue states (D100): counts per state for the list's tabs, and the changes a person makes
// from the console (one issue, or up to MAX_BULK_ISSUES at once). A change refreshes every
// list, count and issue of the project, since an issue moves between tabs.

export const MAX_BULK_ISSUES = 100

export interface IssueCountsParams extends DeviceParams {
  app_id?: string | undefined
  release?: string | undefined
  kind?: IssueKind | undefined
  days: number
}

export const issueCountsQuery = (projectId: string, q: IssueCountsParams) =>
  queryOptions({
    queryKey: ['project', projectId, 'issues', 'counts', q],
    queryFn: async (): Promise<IssueCounts> =>
      unwrap(
        await api.GET('/projects/{projectId}/issues/counts', {
          params: {
            path: { projectId },
            query: {
              ...lastDays(q.days),
              ...(q.app_id ? { app_id: q.app_id } : {}),
              ...(q.release !== undefined ? { release: q.release } : {}),
              ...(q.kind ? { kind: q.kind } : {}),
              ...deviceQuery(q),
            },
          },
        }),
      ),
    staleTime: 30_000,
    // A 409 (too many states to count) or a 403 does not change on a retry.
    retry: (count, error) => !isApiError(error, 409) && !isApiError(error, 403) && count < 2,
  })

function useIssueStatesChanged(projectId: string) {
  const qc = useQueryClient()
  return () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ['project', projectId, 'issues'] }),
      qc.invalidateQueries({ queryKey: ['project', projectId, 'issue'] }),
    ])
}

/** Changes one issue's state or assignee; answers its new state. */
export function useSetIssueState(projectId: string) {
  const changed = useIssueStatesChanged(projectId)
  return useMutation({
    mutationFn: async ({
      issueId,
      change,
    }: {
      issueId: string
      change: IssueStateChange
    }): Promise<{ id: string; state: IssueState }[]> =>
      unwrap(
        await api.PATCH('/projects/{projectId}/issues/{issueId}/state', {
          params: { path: { projectId, issueId } },
          body: change,
        }),
      ).states,
    onSuccess: changed,
  })
}

/** The same change for several issues, in requests of up to MAX_BULK_ISSUES. */
export function useBulkIssueState(projectId: string) {
  const changed = useIssueStatesChanged(projectId)
  return useMutation({
    mutationFn: async ({
      issueIds,
      change,
    }: {
      issueIds: string[]
      change: IssueStateChange
    }): Promise<{ id: string; state: IssueState }[]> => {
      const out: { id: string; state: IssueState }[] = []
      for (let i = 0; i < issueIds.length; i += MAX_BULK_ISSUES) {
        const page = unwrap(
          await api.POST('/projects/{projectId}/issues/state', {
            params: { path: { projectId } },
            body: { ...change, issue_ids: issueIds.slice(i, i + MAX_BULK_ISSUES) },
          }),
        )
        out.push(...page.states)
      }
      return out
    },
    // Settled, not success: a later request may fail after earlier ones changed issues.
    onSettled: changed,
  })
}

/** Merges issues into one (D102); the lists and every issue page read again. */
export function useMergeIssues(projectId: string) {
  const changed = useIssueStatesChanged(projectId)
  return useMutation({
    mutationFn: async ({
      into,
      issueIds,
    }: {
      into: string
      issueIds: string[]
    }): Promise<IssueMergeResult> =>
      unwrap(
        await api.POST('/projects/{projectId}/issues/{issueId}/merge', {
          params: { path: { projectId, issueId: into } },
          body: { issue_ids: issueIds },
        }),
      ),
    onSuccess: changed,
  })
}

/** Gives one fingerprint of an issue its own issue back (D102). */
export function useUnmergeIssue(projectId: string) {
  const changed = useIssueStatesChanged(projectId)
  return useMutation({
    mutationFn: async ({
      issueId,
      fingerprint,
    }: {
      issueId: string
      fingerprint: string
    }): Promise<{ id: string; from: string }> =>
      unwrap(
        await api.POST('/projects/{projectId}/issues/{issueId}/unmerge', {
          params: { path: { projectId, issueId } },
          body: { fingerprint },
        }),
      ),
    onSuccess: changed,
  })
}

export const issueQuery = (projectId: string, issueId: string) =>
  queryOptions({
    queryKey: ['project', projectId, 'issue', issueId],
    queryFn: async (): Promise<IssueDetail> =>
      unwrap(
        await api.GET('/projects/{projectId}/issues/{issueId}', {
          params: { path: { projectId, issueId } },
        }),
      ),
    retry: (count, error) => !(error as { status?: number }).status && count < 2,
  })

/** An issue's occurrences a day, by release, the last `days` (APS-298). */
export const issueSeriesQuery = (
  projectId: string,
  issueId: string,
  q: { app_id?: string | undefined; days: number },
) =>
  queryOptions({
    queryKey: ['project', projectId, 'issue', issueId, 'series', q],
    queryFn: async (): Promise<IssueSeries> =>
      unwrap(
        await api.GET('/projects/{projectId}/issues/{issueId}/series', {
          params: {
            path: { projectId, issueId },
            query: { days: q.days, by: 'release', ...(q.app_id ? { app_id: q.app_id } : {}) },
          },
        }),
      ),
    retry: (count, error) => !(error as { status?: number }).status && count < 2,
  })

export interface MissingSymbolsPage {
  release: string
  /** In-app images the release referenced, with or without symbols. */
  images: number
  missing: MissingSymbol[]
}

/** In-app images of a release without symbols (D73), first 1000. */
export const missingSymbolsQuery = (projectId: string, release: string, appId?: string) =>
  queryOptions({
    queryKey: ['project', projectId, 'missing-symbols', release, appId],
    queryFn: async (): Promise<MissingSymbolsPage> => {
      const page = unwrap(
        await api.GET('/projects/{projectId}/releases/{release}/missing-symbols', {
          params: {
            path: { projectId, release },
            query: { limit: 1000, ...(appId ? { app_id: appId } : {}) },
          },
        }),
      )
      return { release: page.release, images: page.images, missing: page.missing_symbols }
    },
    staleTime: 60_000,
  })

// Release analysis, sessions, vitals and cohorts (D79)

/** One app's crash-free days, every day of the window listed (14 by default). */
export const dailyHealthQuery = (
  projectId: string,
  appId: string | undefined,
  to: string,
  device?: DeviceParams,
) =>
  queryOptions({
    queryKey: ['project', projectId, 'daily', appId, to, deviceQuery(device)],
    queryFn: async (): Promise<ReleaseHealthDay[]> =>
      unwrap(
        await api.GET('/projects/{projectId}/release-health/daily', {
          params: {
            path: { projectId },
            query: { to, ...(appId ? { app_id: appId } : {}), ...deviceQuery(device) },
          },
        }),
      ).days,
    staleTime: 60_000,
  })

/**
 * One release's UTC days, or the app's without `release`, from `from` to `to` (both minute-rounded
 * by the caller so the key stays stable): sessions and crash-free per day, for the charts since a
 * release shipped and its adoption by day (APS-307).
 */
export const releaseDaysQuery = (
  projectId: string,
  appId: string,
  release: string | undefined,
  from: string,
  to: string,
  device?: DeviceParams,
) =>
  queryOptions({
    queryKey: [
      'project',
      projectId,
      'daily',
      appId,
      release ?? null,
      from,
      to,
      deviceQuery(device),
    ],
    queryFn: async (): Promise<ReleaseHealthDay[]> =>
      unwrap(
        await api.GET('/projects/{projectId}/release-health/daily', {
          params: {
            path: { projectId },
            query: {
              from,
              to,
              app_id: appId,
              ...(release !== undefined ? { release } : {}),
              ...deviceQuery(device),
            },
          },
        }),
      ).days,
    staleTime: 60_000,
  })

/** A release's builds, devices and OS versions over the last 30 days (APS-302). */
export const breakdownQuery = (projectId: string, release: string, appId: string) =>
  queryOptions({
    queryKey: ['project', projectId, 'breakdown', release, appId],
    queryFn: async (): Promise<ReleaseBreakdown> =>
      unwrap(
        await api.GET('/projects/{projectId}/releases/{release}/breakdown', {
          params: { path: { projectId, release }, query: { app_id: appId, ...lastDays(30) } },
        }),
      ),
    staleTime: 60_000,
  })

/** Release `b` against its baseline `a`, in one app, over the last 30 days. */
export const compareQuery = (
  projectId: string,
  appId: string,
  a: string,
  b: string,
  device?: DeviceParams,
) =>
  queryOptions({
    queryKey: ['project', projectId, 'compare', appId, a, b, deviceQuery(device)],
    queryFn: async (): Promise<ReleaseComparison> =>
      unwrap(
        await api.GET('/projects/{projectId}/release-health/compare', {
          params: {
            path: { projectId },
            query: { app_id: appId, a, b, limit: 50, ...deviceQuery(device) },
          },
        }),
      ),
    staleTime: 60_000,
  })

/** App start, screen loads and hang and ANR rates of one release of one app. */
export const vitalsQuery = (
  projectId: string,
  appId: string,
  release: string,
  device?: DeviceParams,
) =>
  queryOptions({
    queryKey: ['project', projectId, 'vitals', appId, release, deviceQuery(device)],
    queryFn: async (): Promise<ReleaseVitals | null> => {
      const v = unwrap(
        await api.GET('/projects/{projectId}/vitals', {
          params: {
            path: { projectId },
            query: { app_id: appId, release, screens: 5, ...lastDays(30), ...deviceQuery(device) },
          },
        }),
      )
      return v.releases.find((r) => r.app_id === appId && r.release === release) ?? null
    },
    staleTime: 60_000,
  })

/**
 * Cold start and the hang and ANR rates of one app's releases (the API's top 20 by sessions),
 * over the last 30 days, without screens: the releases table's columns (APS-196).
 */
export const appVitalsQuery = (projectId: string, appId: string, device?: DeviceParams) =>
  queryOptions({
    queryKey: ['project', projectId, 'vitals', appId, 'releases', deviceQuery(device)],
    queryFn: async (): Promise<ReleaseVitals[]> =>
      unwrap(
        await api.GET('/projects/{projectId}/vitals', {
          params: {
            path: { projectId },
            query: { app_id: appId, screens: 0, ...lastDays(30), ...deviceQuery(device) },
          },
        }),
      ).releases.filter((r) => r.app_id === appId),
    staleTime: 60_000,
  })

// How sessions ended (D113 phase 1)

/** One app's sessions by exit reason in a range, overall and per release. */
export const exitsQuery = (
  projectId: string,
  appId: string,
  range: { from: string; to: string },
  key: unknown = range,
  build?: DeviceParams['build'],
) =>
  queryOptions({
    queryKey: ['project', projectId, 'exits', appId, key, build ?? 'release'],
    queryFn: async (): Promise<Exits> =>
      unwrap(
        await api.GET('/projects/{projectId}/exits', {
          params: {
            path: { projectId },
            query: { app_id: appId, ...range, ...deviceQuery({ build }) },
          },
        }),
      ),
    staleTime: 60_000,
  })

/** One app's exit reasons per UTC day, the 14 days to `to` (minute-rounded by the caller). */
export const exitsDailyQuery = (
  projectId: string,
  appId: string,
  to: string,
  build?: DeviceParams['build'],
) =>
  queryOptions({
    queryKey: ['project', projectId, 'exits-daily', appId, to, build ?? 'release'],
    queryFn: async (): Promise<ExitsDaily['days']> =>
      unwrap(
        await api.GET('/projects/{projectId}/exits/daily', {
          params: { path: { projectId }, query: { app_id: appId, to, ...deviceQuery({ build }) } },
        }),
      ).days,
    staleTime: 60_000,
  })

// Performance and network (plan step 3.4)

/**
 * The scope every performance view shares: an app, a release, a window of days, and the device
 * filters (APS-228).
 */
export interface PerfScope extends DeviceParams {
  app_id?: string | undefined
  release?: string | undefined
  days: number
}

/** A performance route's query; `devices: false` for a route that takes no device filter. */
const perfQuery = (scope: PerfScope, devices = true) => ({
  ...lastDays(scope.days),
  ...(scope.app_id ? { app_id: scope.app_id } : {}),
  ...(scope.release !== undefined ? { release: scope.release } : {}),
  ...(devices ? deviceQuery(scope) : {}),
})

/** A funnel over screens (step 3.6), over the perf scope's window (at most 30 days). */
export const funnelQuery = (
  projectId: string,
  scope: PerfScope,
  steps: string[],
  withinMinutes: number | undefined,
) =>
  queryOptions({
    queryKey: ['project', projectId, 'funnel', scope, steps, withinMinutes ?? 0, minute()],
    queryFn: async (): Promise<Funnel> =>
      unwrap(
        await api.GET('/projects/{projectId}/funnels', {
          params: {
            path: { projectId },
            query: {
              ...perfQuery({ ...scope, days: Math.min(scope.days, 30) }, false),
              steps,
              ...(withinMinutes ? { within_minutes: withinMinutes } : {}),
            },
          },
        }),
      ),
    staleTime: 60_000,
  })

/** The paths after or before a screen (step 3.6). */
export const journeysQuery = (
  projectId: string,
  scope: PerfScope,
  screen: string,
  direction: 'next' | 'previous',
  depth: number,
) =>
  queryOptions({
    queryKey: ['project', projectId, 'journeys', scope, screen, direction, depth, minute()],
    queryFn: async (): Promise<Journeys> =>
      unwrap(
        await api.GET('/projects/{projectId}/journeys', {
          params: {
            path: { projectId },
            query: {
              ...perfQuery({ ...scope, days: Math.min(scope.days, 30) }, false),
              screen,
              direction,
              depth,
            },
          },
        }),
      ),
    staleTime: 60_000,
  })

/** One key per minute, so a view's queries share their window. */
const minute = () => Math.floor(Date.now() / 60_000)

export const startQuery = (
  projectId: string,
  scope: PerfScope,
  groupBy: 'release' | 'os_version' | 'device_model' | 'prewarmed' | 'none',
  /** Even-width buckets beside the coarse ones (APS-304), for Console D's histograms. */
  fine = false,
) =>
  queryOptions({
    queryKey: ['project', projectId, 'perf-start', scope, groupBy, fine, minute()],
    queryFn: async (): Promise<AppStart> =>
      unwrap(
        await api.GET('/projects/{projectId}/performance/start', {
          params: {
            path: { projectId },
            query: {
              ...perfQuery(scope),
              group_by: groupBy,
              ...(fine ? { buckets: 'fine' as const } : {}),
            },
          },
        }),
      ),
    staleTime: 60_000,
  })

/** Every screen's loads with the totals over all of them (APS-304), for the screens list. */
export const screensListQuery = (
  projectId: string,
  scope: PerfScope,
  sort: 'loads' | 'ttid_p90' | 'ttfd_p90' = 'loads',
  limit = 50,
) =>
  queryOptions({
    queryKey: ['project', projectId, 'perf-screens-list', scope, sort, limit, minute()],
    queryFn: async (): Promise<Screens> =>
      unwrap(
        await api.GET('/projects/{projectId}/performance/screens', {
          params: { path: { projectId }, query: { ...perfQuery(scope), sort, limit } },
        }),
      ),
    staleTime: 60_000,
  })

export const screensQuery = (
  projectId: string,
  scope: PerfScope,
  sort: 'loads' | 'ttid_p90' | 'ttfd_p90' = 'loads',
  limit = 50,
) =>
  queryOptions({
    queryKey: ['project', projectId, 'perf-screens', scope, sort, limit, minute()],
    queryFn: async (): Promise<ScreenSummary[]> =>
      unwrap(
        await api.GET('/projects/{projectId}/performance/screens', {
          params: { path: { projectId }, query: { ...perfQuery(scope), sort, limit } },
        }),
      ).screens,
    staleTime: 60_000,
  })

export const screenQuery = (
  projectId: string,
  appId: string,
  screen: string,
  scope: PerfScope,
  /** Even 100 ms buckets for TTID and TTFD (APS-304). */
  fine = false,
) =>
  queryOptions({
    queryKey: ['project', projectId, 'perf-screen', appId, screen, scope, fine, minute()],
    queryFn: async (): Promise<ScreenDetail> =>
      unwrap(
        await api.GET('/projects/{projectId}/performance/screens/detail', {
          params: {
            path: { projectId },
            query: {
              ...perfQuery({ ...scope, app_id: appId }),
              app_id: appId,
              screen,
              ...(fine ? { buckets: 'fine' as const } : {}),
            },
          },
        }),
      ),
    staleTime: 60_000,
  })

export const hangsQuery = (
  projectId: string,
  scope: PerfScope,
  fine = false,
  /** `release` splits each screen row by release (APS-304). */
  by: 'screen' | 'release' = 'screen',
) =>
  queryOptions({
    queryKey: ['project', projectId, 'perf-hangs', scope, fine, by, minute()],
    queryFn: async (): Promise<Hangs> =>
      unwrap(
        await api.GET('/projects/{projectId}/performance/hangs', {
          params: {
            path: { projectId },
            query: {
              ...perfQuery(scope),
              ...(fine ? { buckets: 'fine' as const } : {}),
              ...(by === 'release' ? { by } : {}),
            },
          },
        }),
      ),
    staleTime: 60_000,
  })

export const framesQuery = (
  projectId: string,
  scope: PerfScope,
  sort: 'minutes' | 'slow_per_minute' | 'frozen_per_minute' = 'minutes',
  limit = 50,
) =>
  queryOptions({
    queryKey: ['project', projectId, 'perf-frames', scope, sort, limit, minute()],
    queryFn: async (): Promise<Frames> =>
      unwrap(
        await api.GET('/projects/{projectId}/performance/frames', {
          params: { path: { projectId }, query: { ...perfQuery(scope), sort, limit } },
        }),
      ),
    staleTime: 60_000,
  })

/** The resources routes' range and app (APS-462); none takes the device filters. */
const resourcesQuery_ = (scope: PerfScope) => ({
  ...lastDays(scope.days),
  ...(scope.app_id ? { app_id: scope.app_id } : {}),
})

/** Device resources per release, and installs by free space (D113 phase 2, APS-462). */
export const resourcesQuery = (projectId: string, scope: PerfScope) =>
  queryOptions({
    queryKey: ['project', projectId, 'resources', scope, minute()],
    queryFn: async (): Promise<Resources> =>
      unwrap(
        await api.GET('/projects/{projectId}/resources', {
          params: { path: { projectId }, query: resourcesQuery_(scope) },
        }),
      ),
    staleTime: 60_000,
  })

/** Memory per screen in the scope's release, with a baseline release's p90. */
export const resourceScreensQuery = (projectId: string, scope: PerfScope, baseline?: string) =>
  queryOptions({
    queryKey: ['project', projectId, 'resources-screens', scope, baseline, minute()],
    queryFn: async () =>
      unwrap(
        await api.GET('/projects/{projectId}/resources/screens', {
          params: {
            path: { projectId },
            query: {
              ...resourcesQuery_(scope),
              ...(scope.release !== undefined ? { release: scope.release } : {}),
              ...(baseline !== undefined ? { baseline } : {}),
            },
          },
        }),
      ),
    staleTime: 60_000,
  })

/** Memory p90 per day and release, on one screen or all. */
export const memoryDailyQuery = (projectId: string, scope: PerfScope, screen?: string) =>
  queryOptions({
    queryKey: ['project', projectId, 'resources-memory-daily', scope, screen, minute()],
    queryFn: async (): Promise<MemoryDaily> =>
      unwrap(
        await api.GET('/projects/{projectId}/resources/memory/daily', {
          params: {
            path: { projectId },
            query: { ...resourcesQuery_(scope), ...(screen !== undefined ? { screen } : {}) },
          },
        }),
      ),
    staleTime: 60_000,
  })

/** Battery drain per screen from captured sessions' snapshots, with a baseline release's. */
export const resourceBatteryQuery = (projectId: string, scope: PerfScope, baseline?: string) =>
  queryOptions({
    queryKey: ['project', projectId, 'resources-battery', scope, baseline, minute()],
    queryFn: async (): Promise<ResourcesBattery> =>
      unwrap(
        await api.GET('/projects/{projectId}/resources/battery', {
          params: {
            path: { projectId },
            query: {
              ...resourcesQuery_(scope),
              ...(scope.release !== undefined ? { release: scope.release } : {}),
              ...(baseline !== undefined ? { baseline } : {}),
            },
          },
        }),
      ),
    staleTime: 60_000,
  })

/** Thermal state from captured sessions' snapshots, and low power from every session. */
export const resourceThermalQuery = (projectId: string, scope: PerfScope) =>
  queryOptions({
    queryKey: ['project', projectId, 'resources-thermal', scope, minute()],
    queryFn: async (): Promise<ResourcesThermal> =>
      unwrap(
        await api.GET('/projects/{projectId}/resources/thermal', {
          params: {
            path: { projectId },
            query: {
              ...resourcesQuery_(scope),
              ...(scope.release !== undefined ? { release: scope.release } : {}),
            },
          },
        }),
      ),
    staleTime: 60_000,
  })

/** One captured session's resource snapshots, oldest first. */
export const sessionResourcesQuery = (projectId: string, sessionId: string) =>
  queryOptions({
    queryKey: ['project', projectId, 'session', sessionId, 'resources'],
    queryFn: async (): Promise<SessionResources> =>
      unwrap(
        await api.GET('/projects/{projectId}/sessions/{sessionId}/resources', {
          params: { path: { projectId, sessionId } },
        }),
      ),
    staleTime: 60_000,
  })

export const seriesQuery = (
  projectId: string,
  appId: string,
  metric: SeriesMetric,
  days: number,
  by: 'day' | 'release' = 'day',
  device?: DeviceParams,
) =>
  queryOptions({
    queryKey: [
      'project',
      projectId,
      'perf-series',
      appId,
      metric,
      days,
      by,
      deviceQuery(device),
      minute(),
    ],
    queryFn: async (): Promise<PerformanceSeries> =>
      unwrap(
        await api.GET('/projects/{projectId}/performance/series', {
          params: {
            path: { projectId },
            query: { ...lastDays(days), app_id: appId, metric, by, ...deviceQuery(device) },
          },
        }),
      ),
    staleTime: 60_000,
  })

export const metricKitQuery = (
  projectId: string,
  appId: string,
  days: number,
  metric: string,
  device?: DeviceParams,
) =>
  queryOptions({
    queryKey: [
      'project',
      projectId,
      'metrickit',
      appId,
      days,
      metric,
      deviceQuery(device),
      minute(),
    ],
    queryFn: async (): Promise<MetricKitHistograms> =>
      unwrap(
        await api.GET('/projects/{projectId}/metrics/metrickit', {
          params: {
            path: { projectId },
            query: { ...lastDays(days), app_id: appId, metric, ...deviceQuery(device) },
          },
        }),
      ),
    staleTime: 60_000,
  })

export const endpointsQuery = (
  projectId: string,
  scope: PerfScope,
  sort: 'requests' | 'error_rate' | 'p90' = 'requests',
  limit = 50,
  /** Leave out endpoints with fewer requests, so one failure on a rare one does not lead. */
  minRequests?: number,
  /** Each endpoint's last 14 days, for its sparkline (APS-298). */
  daily = false,
) =>
  queryOptions({
    queryKey: [
      'project',
      projectId,
      'endpoints',
      scope,
      sort,
      limit,
      minRequests ?? 0,
      daily,
      minute(),
    ],
    queryFn: async (): Promise<Endpoints> =>
      unwrap(
        await api.GET('/projects/{projectId}/network/endpoints', {
          params: {
            path: { projectId },
            query: {
              ...perfQuery(scope),
              sort,
              limit,
              ...(minRequests ? { min_requests: minRequests } : {}),
              ...(daily ? { daily: 'true' as const } : {}),
            },
          },
        }),
      ),
    staleTime: 60_000,
  })

export interface EndpointKey {
  method: string
  host: string
  template: string
}

export const endpointQuery = (
  projectId: string,
  appId: string,
  endpoint: EndpointKey,
  scope: PerfScope,
  /** Even 100 ms latency buckets (APS-304). */
  fine = false,
) =>
  queryOptions({
    queryKey: ['project', projectId, 'endpoint', appId, endpoint, scope, fine, minute()],
    queryFn: async (): Promise<EndpointDetail> =>
      unwrap(
        await api.GET('/projects/{projectId}/network/endpoints/detail', {
          params: {
            path: { projectId },
            query: {
              ...perfQuery({ ...scope, app_id: appId }),
              app_id: appId,
              ...endpoint,
              ...(fine ? { buckets: 'fine' as const } : {}),
            },
          },
        }),
      ),
    staleTime: 60_000,
  })

export const traceQuery = (projectId: string, traceId: string) =>
  queryOptions({
    queryKey: ['project', projectId, 'trace', traceId],
    queryFn: async (): Promise<Trace> =>
      unwrap(
        await api.GET('/projects/{projectId}/traces/{traceId}', {
          params: { path: { projectId, traceId } },
        }),
      ),
    retry: (count, error) => !(error as { status?: number }).status && count < 2,
  })

export const OUTCOME_FILTERS = ['crashed', 'abnormal_exit', 'ok'] as const
export type OutcomeFilter = (typeof OUTCOME_FILTERS)[number]

export interface SessionFilters {
  app_id?: string | undefined
  release?: string | undefined
  outcome?: OutcomeFilter | undefined
  device_model?: string | undefined
  os_version?: string | undefined
  /** Which builds (APS-508): release ones unless `debug` or `all`. */
  build?: DeviceParams['build']
  has_error?: boolean | undefined
  /** Sessions with (true) or without (false) a stored replay (APS-196). */
  has_replay?: boolean | undefined
  issue_id?: string | undefined
  /** Sessions with a request to this endpoint (step 3.4), optionally failed ones only. */
  endpoint?: (EndpointKey & { failed?: boolean | undefined }) | undefined
  /** Sessions that loaded this screen, optionally slowly. */
  screen?: string | undefined
  min_ttid_ms?: number | undefined
  min_ttfd_ms?: number | undefined
  /** Sessions with a failed request to any endpoint (APS-301). */
  has_failed_request?: boolean | undefined
  /** Sessions where this feature flag (variant) was evaluated (step 3.6). */
  flag?: string | undefined
  flag_variant?: string | undefined
  /** Sessions whose last screen load was this screen: where they stopped (APS-301). */
  stopped?: string | undefined
  /** One app user's sessions: the user.hash the app sends (D114). */
  user?: string | undefined
}

/** How the list is sorted (APS-301): newest, longest or most errors first, or the reverse. */
export interface SessionSort {
  sort?: 'started' | 'length' | 'errors' | undefined
  order?: 'desc' | 'asc' | undefined
}

const bool = (b: boolean) => (b ? 'true' : 'false') as 'true' | 'false'

/** The query parameters both the list and its counts take from the filters. */
function sessionParams(f: SessionFilters & { days: number }) {
  return {
    ...lastDays(f.days),
    ...(f.app_id ? { app_id: f.app_id } : {}),
    ...(f.release ? { release: f.release } : {}),
    ...(f.outcome ? { outcome: f.outcome } : {}),
    ...(f.device_model ? { device_model: f.device_model } : {}),
    ...(f.os_version ? { os_version: f.os_version } : {}),
    ...(f.build ? { build: f.build } : {}),
    ...(f.has_error !== undefined ? { has_error: bool(f.has_error) } : {}),
    ...(f.has_replay !== undefined ? { has_replay: bool(f.has_replay) } : {}),
    ...(f.issue_id ? { issue_id: f.issue_id } : {}),
    ...(f.endpoint
      ? {
          endpoint_method: f.endpoint.method,
          endpoint_host: f.endpoint.host,
          endpoint_template: f.endpoint.template,
          ...(f.endpoint.failed ? { endpoint_failed: 'true' as const } : {}),
        }
      : {}),
    ...(f.screen ? { screen: f.screen } : {}),
    ...(f.screen && f.min_ttid_ms !== undefined ? { min_ttid_ms: f.min_ttid_ms } : {}),
    ...(f.screen && f.min_ttfd_ms !== undefined ? { min_ttfd_ms: f.min_ttfd_ms } : {}),
    ...(f.has_failed_request ? { has_failed_request: 'true' as const } : {}),
    ...(f.flag ? { flag: f.flag } : {}),
    ...(f.flag && f.flag_variant !== undefined ? { flag_variant: f.flag_variant } : {}),
    ...(f.stopped ? { stopped: f.stopped } : {}),
    ...(f.user ? { user: f.user } : {}),
  }
}

export interface SessionsPage {
  sessions: SessionListItem[]
  next_cursor: string | null
}

/** Sessions that started in the last `days` (at most 30), newest first unless sorted. */
export async function fetchSessions(
  projectId: string,
  f: SessionFilters & SessionSort & { days: number; limit?: number },
  cursor?: string,
): Promise<SessionsPage> {
  return unwrap(
    await api.GET('/projects/{projectId}/sessions', {
      params: {
        path: { projectId },
        query: {
          ...sessionParams(f),
          limit: f.limit ?? 50,
          ...(f.sort && f.sort !== 'started' ? { sort: f.sort } : {}),
          ...(f.order === 'asc' ? { order: 'asc' as const } : {}),
          ...(cursor ? { cursor } : {}),
        },
      },
    }),
  )
}

/** How many sessions match the filters, by outcome, with errors and replays, and their length. */
export const sessionCountsQuery = (projectId: string, f: SessionFilters & { days: number }) =>
  queryOptions({
    queryKey: ['project', projectId, 'sessions', 'summary', f],
    queryFn: async (): Promise<SessionCounts> =>
      unwrap(
        await api.GET('/projects/{projectId}/sessions/summary', {
          params: { path: { projectId }, query: sessionParams(f) },
        }),
      ),
    staleTime: 60_000,
  })

/**
 * The device models and OS versions of the sessions that match the filters (APS-400): the top 20
 * of each, each counted without its own filter, and whether there are more.
 */
export const sessionFacetsQuery = (projectId: string, f: SessionFilters & { days: number }) =>
  queryOptions({
    queryKey: ['project', projectId, 'sessions', 'facets', f],
    queryFn: async (): Promise<SessionFacets> =>
      unwrap(
        await api.GET('/projects/{projectId}/sessions/facets', {
          params: { path: { projectId }, query: sessionParams(f) },
        }),
      ),
    staleTime: 60_000,
  })

/** The newest sessions a trigger released in the last `days`: a rule's "sessions it sent" (D114). */
export const triggerSessionsQuery = (
  projectId: string,
  appId: string,
  trigger: string,
  days = 7,
  limit = 5,
) =>
  queryOptions({
    queryKey: ['project', projectId, 'sessions', 'trigger', appId, trigger, days, limit],
    queryFn: async (): Promise<SessionsPage> =>
      unwrap(
        await api.GET('/projects/{projectId}/sessions', {
          params: {
            path: { projectId },
            query: { app_id: appId, trigger, limit, ...lastDays(days) },
          },
        }),
      ),
    staleTime: 60_000,
  })

/**
 * How many sessions each of an app's rules sent in the last `days`, and the newest (D114): one call
 * for every rule of the list; a rule absent from it sent none.
 */
export const ruleStatsListQuery = (projectId: string, appId: string, days = 7) =>
  queryOptions({
    queryKey: ['project', projectId, 'rules', 'stats', appId, days],
    queryFn: async (): Promise<RuleStatsList> =>
      unwrap(
        await api.GET('/projects/{projectId}/rules/stats', {
          params: { path: { projectId }, query: { app_id: appId, ...lastDays(days) } },
        }),
      ),
    staleTime: 60_000,
  })

/** One rule's sessions in the last `days` (D114): the count, the newest, per day and by outcome. */
export const ruleStatsQuery = (projectId: string, appId: string, ruleId: string, days = 7) =>
  queryOptions({
    queryKey: ['project', projectId, 'rules', ruleId, 'stats', appId, days],
    queryFn: async (): Promise<RuleStats> =>
      unwrap(
        await api.GET('/projects/{projectId}/rules/{ruleId}/stats', {
          params: { path: { projectId, ruleId }, query: { app_id: appId, ...lastDays(days) } },
        }),
      ),
    staleTime: 60_000,
  })

/** The feature flags evaluated in the last `days` (at most 30), with up to 20 variants each. */
export const flagsQuery = (projectId: string, appId: string | undefined, days: number) =>
  queryOptions({
    queryKey: ['project', projectId, 'flags', appId ?? '', Math.min(days, 30)],
    queryFn: async (): Promise<FeatureFlags> =>
      unwrap(
        await api.GET('/projects/{projectId}/flags', {
          params: {
            path: { projectId },
            query: {
              ...lastDays(Math.min(days, 30)),
              variants: 20,
              ...(appId ? { app_id: appId } : {}),
            },
          },
        }),
      ),
    staleTime: 60_000,
  })

/** An issue's occurrences by feature flag variant (last 30 days). */
export const issueFlagsQuery = (projectId: string, issueId: string, appId: string) =>
  queryOptions({
    queryKey: ['project', projectId, 'issue-flags', issueId, appId],
    queryFn: async (): Promise<IssueFlags> =>
      unwrap(
        await api.GET('/projects/{projectId}/issues/{issueId}/flags', {
          params: {
            path: { projectId, issueId },
            query: { app_id: appId, variants: 5, ...lastDays(30) },
          },
        }),
      ),
    staleTime: 60_000,
  })

/** What the sessions of an issue share, against the app's other sessions (last 30 days). */
export const issueCohortQuery = (projectId: string, issueId: string, appId: string) =>
  queryOptions({
    queryKey: ['project', projectId, 'cohort', issueId, appId],
    queryFn: async (): Promise<CohortComparison> =>
      unwrap(
        await api.GET('/projects/{projectId}/cohorts/compare', {
          params: {
            path: { projectId },
            // The issue whole, every build: the issue page shows it so (APS-508).
            query: { issue_id: issueId, app_id: appId, limit: 3, ...lastDays(30), build: 'all' },
          },
        }),
      ),
    staleTime: 60_000,
  })

/** The whole timeline: pages of up to 1000 events, at most 5000 events. */
export async function fetchSession(projectId: string, sessionId: string): Promise<SessionTimeline> {
  let cursor: string | undefined
  let first: SessionTimeline | undefined
  do {
    const page = unwrap(
      await api.GET('/projects/{projectId}/sessions/{sessionId}', {
        params: {
          path: { projectId, sessionId },
          query: { limit: 1000, ...(cursor ? { cursor } : {}) },
        },
      }),
    )
    if (first) first.events.push(...page.events)
    else first = page
    first.next_cursor = page.next_cursor
    cursor = page.next_cursor ?? undefined
  } while (cursor && first.events.length < 5000)
  return first
}

export const sessionQuery = (projectId: string, sessionId: string) =>
  queryOptions({
    queryKey: ['project', projectId, 'session', sessionId],
    queryFn: () => fetchSession(projectId, sessionId),
    retry: (count, error) => !(error as { status?: number }).status && count < 2,
  })

/** At most two hours of replay: segments are about 10 seconds each (spec 10.2). */
export const MAX_REPLAY_SEGMENTS = 720

/** A session's replay segments in time order (spec 10.2): pages of 1000, at most 720. */
export async function fetchReplaySegments(
  projectId: string,
  sessionId: string,
): Promise<ReplaySegment[]> {
  const out: ReplaySegment[] = []
  let cursor: string | undefined
  do {
    const page = unwrap(
      await api.GET('/projects/{projectId}/sessions/{sessionId}/replay/segments', {
        params: {
          path: { projectId, sessionId },
          query: { limit: 1000, ...(cursor ? { cursor } : {}) },
        },
      }),
    )
    out.push(...page.segments)
    cursor = page.next_cursor ?? undefined
  } while (cursor && out.length < MAX_REPLAY_SEGMENTS)
  return out.slice(0, MAX_REPLAY_SEGMENTS)
}

export const replaySegmentsQuery = (projectId: string, sessionId: string) =>
  queryOptions({
    queryKey: ['project', projectId, 'session', sessionId, 'replay'],
    queryFn: () => fetchReplaySegments(projectId, sessionId),
    retry: (count, error) => !(error as { status?: number }).status && count < 2,
  })

/**
 * One attachment as stored (a replay segment's JSON), through the read API: the API checks the
 * project and proxies the object, so the browser never sees an R2 URL. Parsed, not checked:
 * the player checks it (lib/replay.ts).
 */
export async function fetchAttachment(
  projectId: string,
  attachmentId: string,
  appId: string,
): Promise<unknown> {
  return unwrap(
    await api.GET('/projects/{projectId}/attachments/{attachmentId}', {
      params: { path: { projectId, attachmentId }, query: { app_id: appId } },
    }),
  )
}

// Mutations

export function useCreateOrg() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (name: string) => {
      // Where the sign-up came from (APS-435): the API records it on the first organization only.
      const origin = signupHandoff()
      const attribution = {
        ...(origin?.utmSource ? { utm_source: origin.utmSource } : {}),
        ...(origin?.referringDomain ? { referring_domain: origin.referringDomain } : {}),
      }
      return typed(
        api.POST('/orgs', {
          body: { name, ...(Object.keys(attribution).length > 0 ? { attribution } : {}) },
        }),
      )
    },
    onSuccess: () => {
      clearSignupHandoff()
      return qc.invalidateQueries({ queryKey: ['me'] })
    },
  })
}

export function useCreateProject(orgId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (name: string) =>
      typed(api.POST('/orgs/{orgId}/projects', { params: { path: { orgId } }, body: { name } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['org', orgId, 'projects'] }),
  })
}

export function useCreateApp(orgId: string, projectId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: { platform: Platform; name: string; bundle_id: string }) =>
      typed(
        api.POST('/orgs/{orgId}/projects/{projectId}/apps', {
          params: { path: { orgId, projectId } },
          body: input,
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: appsQuery(orgId, projectId).queryKey }),
  })
}

/** Renames the project (owners and admins; audited as project.renamed; APS-394). */
export function useRenameProject(orgId: string, projectId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (name: string) =>
      typed(
        api.PATCH('/orgs/{orgId}/projects/{projectId}', {
          params: { path: { orgId, projectId } },
          body: { name },
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['org', orgId, 'projects'] }),
  })
}

/** Sets or clears the project's APM link template (owners and admins; audited). */
export function useUpdateApmLink(orgId: string, projectId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (template: string | null) =>
      typed(
        api.PATCH('/orgs/{orgId}/projects/{projectId}', {
          params: { path: { orgId, projectId } },
          body: { apm_trace_url_template: template },
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['org', orgId, 'projects'] }),
  })
}

/** An app's instrumentation (D111): what its devices send, per family, and keep. */
export const instrumentationQuery = (orgId: string, projectId: string, appId: string) =>
  queryOptions({
    queryKey: ['org', orgId, 'project', projectId, 'app', appId, 'instrumentation'],
    queryFn: () =>
      typed(
        api.GET('/orgs/{orgId}/projects/{projectId}/apps/{appId}/instrumentation', {
          params: { path: { orgId, projectId, appId } },
        }),
      ),
  })

/** Publishes an app's instrumentation: devices take it at their next policy fetch. */
export function useSaveInstrumentation(orgId: string, projectId: string, appId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: InstrumentationInput) =>
      typed(
        api.PUT('/orgs/{orgId}/projects/{projectId}/apps/{appId}/instrumentation', {
          params: { path: { orgId, projectId, appId } },
          body,
        }),
      ),
    onSuccess: (data) =>
      qc.setQueryData(instrumentationQuery(orgId, projectId, appId).queryKey, data),
  })
}

/** Stops the SDK on every device of the app, or starts it again (spec 12.3); a reason to stop. */
export function useSetKillSwitch(orgId: string, projectId: string, appId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { on: boolean; reason?: string }) =>
      typed(
        api.POST('/orgs/{orgId}/projects/{projectId}/apps/{appId}/kill-switch', {
          params: { path: { orgId, projectId, appId } },
          body,
        }),
      ),
    onSuccess: (data) =>
      qc.setQueryData(instrumentationQuery(orgId, projectId, appId).queryKey, data),
  })
}

/** The app's layers (D114, spec 12.4): the active ones in policy order, then the history. */
export const layersQuery = (orgId: string, projectId: string, appId: string) =>
  queryOptions({
    queryKey: ['org', orgId, 'project', projectId, 'app', appId, 'layers'],
    queryFn: () =>
      typed(
        api.GET('/orgs/{orgId}/projects/{projectId}/apps/{appId}/layers', {
          params: { path: { orgId, projectId, appId } },
        }),
      ),
  })

/** Publishes the app's active layers whole and in order; the API audits the change. */
export function useSaveLayers(orgId: string, projectId: string, appId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (layers: LayerInput[]) =>
      typed(
        api.PUT('/orgs/{orgId}/projects/{projectId}/apps/{appId}/layers', {
          params: { path: { orgId, projectId, appId } },
          body: { layers },
        }),
      ),
    onSuccess: (data) => qc.setQueryData(layersQuery(orgId, projectId, appId).queryKey, data),
  })
}

/** A layer's applied devices against its holdback over the last `days` (1 to 30). */
export const layerComparisonQuery = (
  orgId: string,
  projectId: string,
  appId: string,
  layerId: string,
  days = 7,
) =>
  queryOptions({
    queryKey: [
      'org',
      orgId,
      'project',
      projectId,
      'app',
      appId,
      'layer',
      layerId,
      'comparison',
      days,
    ],
    queryFn: () =>
      typed(
        api.GET('/orgs/{orgId}/projects/{projectId}/apps/{appId}/layers/{layerId}/comparison', {
          params: { path: { orgId, projectId, appId, layerId }, query: { days } },
        }),
      ),
  })

/** Stops one layer now, with the reason the history keeps: the next policy leaves it out. */
export function useStopLayer(orgId: string, projectId: string, appId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ layerId, reason }: { layerId: string; reason: string }) =>
      typed(
        api.POST('/orgs/{orgId}/projects/{projectId}/apps/{appId}/layers/{layerId}/stop', {
          params: { path: { orgId, projectId, appId, layerId } },
          body: { reason },
        }),
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: layersQuery(orgId, projectId, appId).queryKey }),
  })
}

/** The sessions a capture request released (spec 15.5), newest first, at most `limit`. */
export const captureSessionsQuery = (
  projectId: string,
  requestId: string,
  limit: number,
  days = 30,
) =>
  queryOptions({
    queryKey: ['project', projectId, 'sessions', 'capture', requestId, limit, days],
    queryFn: async (): Promise<SessionsPage> =>
      unwrap(
        await api.GET('/projects/{projectId}/sessions', {
          params: {
            path: { projectId },
            query: { capture_request: requestId, limit, ...lastDays(days) },
          },
        }),
      ),
    staleTime: 60_000,
  })

/** The project's remote requests to devices (D111), newest first; with `user`, that app user's. */
export const deviceRequestsQuery = (orgId: string, projectId: string, user?: string) =>
  queryOptions({
    queryKey: ['org', orgId, 'project', projectId, 'device-requests', ...(user ? [user] : [])],
    queryFn: async () =>
      (
        await typed(
          api.GET('/orgs/{orgId}/projects/{projectId}/device-requests', {
            params: { path: { orgId, projectId }, ...(user ? { query: { user } } : {}) },
          }),
        )
      ).requests,
  })

export interface NewDeviceRequest {
  kind: 'send' | 'delete' | 'capture'
  /** For a capture: the next sessions sent in full, 1 to 20 (spec 15.5). */
  sessions?: number
  subject_kind: 'install' | 'user'
  subject: string
  /** Why, as Activity logs it (APS-454): 1 to 200 characters, no email in it. */
  reason: string
  families?: DeviceRequest['families'] & {}
  days?: number
}

/** Asks one install or one app user to send what its device holds, or to delete it. */
export function useCreateDeviceRequest(orgId: string, projectId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: NewDeviceRequest) =>
      typed(
        api.POST('/orgs/{orgId}/projects/{projectId}/device-requests', {
          params: { path: { orgId, projectId } },
          body,
        }),
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: deviceRequestsQuery(orgId, projectId).queryKey }),
  })
}

/** Takes an open request out of the policies. */
export function useCancelDeviceRequest(orgId: string, projectId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (requestId: string) =>
      typed(
        api.POST('/orgs/{orgId}/projects/{projectId}/device-requests/{requestId}/cancel', {
          params: { path: { orgId, projectId, requestId } },
        }),
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: deviceRequestsQuery(orgId, projectId).queryKey }),
  })
}

/** The organization's projects being deleted (APS-401): owners and admins. */
export const projectErasuresQuery = (orgId: string) =>
  queryOptions({
    queryKey: ['org', orgId, 'project-erasures'],
    queryFn: () =>
      typed(api.GET('/orgs/{orgId}/project-erasures', { params: { path: { orgId } } })),
    retry: (count, error) => !isApiError(error, 403) && count < 2,
  })

/** Deletes a project (APS-401): `confirm` is its exact name; gone from every list at once. */
export function useScheduleProjectErasure(orgId: string, projectId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (confirm: string) =>
      typed(
        api.POST('/orgs/{orgId}/projects/{projectId}/erasure', {
          params: { path: { orgId, projectId } },
          body: { confirm },
        }),
      ),
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: projectsQuery(orgId).queryKey }),
        qc.invalidateQueries({ queryKey: projectErasuresQuery(orgId).queryKey }),
      ]),
  })
}

/**
 * Cancels a project's deletion while it is scheduled: the project and its keys are back, and so
 * is its page, without a reload. `afterCancel` (a move to the project) and the toast (`done`,
 * flash.ts) come first, here and not in the caller's own onSuccess: the refetch below removes the
 * row that canceled, and a mutate's own callbacks do not run once its component is gone. Then
 * every query of the project is fetched again, the ones that answered 410 erasing (APS-406) among
 * them, so the shell's note gives way. Not `then`: an object with one would look like a promise.
 */
export function useCancelProjectErasure(orgId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: { projectId: string; done: string; afterCancel?: () => unknown }) =>
      noContent(
        api.POST('/orgs/{orgId}/projects/{projectId}/erasure/cancel', {
          params: { path: { orgId, projectId: input.projectId } },
        }),
      ),
    onSuccess: async (_, { projectId, done, afterCancel }) => {
      await afterCancel?.()
      flash(done)
      await Promise.all([
        qc.invalidateQueries({ queryKey: projectsQuery(orgId).queryKey }),
        qc.invalidateQueries({ queryKey: projectErasuresQuery(orgId).queryKey }),
        qc.invalidateQueries({ predicate: (q) => q.queryKey.includes(projectId) }),
      ])
    },
  })
}

// People (D114 #6): one app user's devices, and the app users the team pinned.

/** One app user's latest sessions in the last `days`, newest first (one key a minute). */
export const appUserSessionsQuery = (
  projectId: string,
  hash: string,
  days: number,
  limit: number,
) =>
  queryOptions({
    queryKey: ['project', projectId, 'sessions', 'user', hash, days, limit, minute()],
    queryFn: () => fetchSessions(projectId, { user: hash, days, limit }),
    staleTime: 60_000,
  })

/** One app user's devices in the last 30 days (the API's most), the latest first, at most 50. */
export const appUserQuery = (projectId: string, hash: string) =>
  queryOptions({
    queryKey: ['project', projectId, 'app-user', hash, minute()],
    queryFn: async (): Promise<{ devices: AppUserDevice[]; more: boolean }> =>
      unwrap(
        await api.GET('/projects/{projectId}/app-user', {
          params: { path: { projectId }, query: { hash, ...lastDays(30) } },
        }),
      ),
    staleTime: 60_000,
  })

/** The app users the team pinned, the oldest first (at most `max`). Members. */
export const appUserPinsQuery = (orgId: string, projectId: string) =>
  queryOptions({
    queryKey: ['org', orgId, 'project', projectId, 'app-user-pins'],
    queryFn: () =>
      typed(
        api.GET('/orgs/{orgId}/projects/{projectId}/app-user-pins', {
          params: { path: { orgId, projectId } },
        }),
      ),
  })

/** Pins an app user for the team, renames the pin or unpins it; each is audited. */
export function useAppUserPin(orgId: string, projectId: string) {
  const qc = useQueryClient()
  const onSuccess = () =>
    qc.invalidateQueries({ queryKey: appUserPinsQuery(orgId, projectId).queryKey })
  const pin = useMutation({
    mutationFn: (body: { user_hash: string; label: string }) =>
      typed(
        api.POST('/orgs/{orgId}/projects/{projectId}/app-user-pins', {
          params: { path: { orgId, projectId } },
          body,
        }),
      ),
    onSuccess,
  })
  const rename = useMutation({
    mutationFn: ({ pinId, label }: { pinId: string; label: string }) =>
      typed(
        api.PATCH('/orgs/{orgId}/projects/{projectId}/app-user-pins/{pinId}', {
          params: { path: { orgId, projectId, pinId } },
          body: { label },
        }),
      ),
    onSuccess,
  })
  const unpin = useMutation({
    mutationFn: (pinId: string) =>
      noContent(
        api.DELETE('/orgs/{orgId}/projects/{projectId}/app-user-pins/{pinId}', {
          params: { path: { orgId, projectId, pinId } },
        }),
      ),
    onSuccess,
  })
  return { pin, rename, unpin }
}

/** The project's in-app lists, one per kind of frame (D80 addendum, APS-473). Members. */
export const inAppListsQuery = (orgId: string, projectId: string) =>
  queryOptions({
    queryKey: ['org', orgId, 'project', projectId, 'in-app'],
    queryFn: () =>
      typed(
        api.GET('/orgs/{orgId}/projects/{projectId}/in-app', {
          params: { path: { orgId, projectId } },
        }),
      ),
  })

/** Sets the in-app lists of the kinds given (owners and admins; audited). */
export function useSetInAppLists(orgId: string, projectId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (lists: { kind: InAppKind; in_app: string[]; third_party: string[] }[]) =>
      typed(
        api.PUT('/orgs/{orgId}/projects/{projectId}/in-app', {
          params: { path: { orgId, projectId } },
          body: { lists },
        }),
      ),
    onSuccess: (data) => qc.setQueryData(inAppListsQuery(orgId, projectId).queryKey, data),
  })
}

/** The project's app user erasures (D92): owners and admins. */
export const userErasuresQuery = (orgId: string, projectId: string) =>
  queryOptions({
    queryKey: ['org', orgId, 'project', projectId, 'user-erasures'],
    queryFn: () =>
      typed(
        api.GET('/orgs/{orgId}/projects/{projectId}/user-erasures', {
          params: { path: { orgId, projectId } },
        }),
      ),
    retry: (count, error) => !isApiError(error, 403) && count < 2,
  })

/** Schedules erasing one app user by their user.hash (a day's grace; audited without it). */
export function useScheduleUserErasure(orgId: string, projectId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: { user_hash: string; reason: string }) =>
      typed(
        api.POST('/orgs/{orgId}/projects/{projectId}/user-erasures', {
          params: { path: { orgId, projectId } },
          body: input,
        }),
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: userErasuresQuery(orgId, projectId).queryKey }),
  })
}

export function useCancelUserErasure(orgId: string, projectId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (erasureId: string) =>
      noContent(
        api.POST('/orgs/{orgId}/projects/{projectId}/user-erasures/{erasureId}/cancel', {
          params: { path: { orgId, projectId, erasureId } },
        }),
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: userErasuresQuery(orgId, projectId).queryKey }),
  })
}

export function useRotateKey(orgId: string, projectId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: { appId: string; revokePrevious: boolean }) =>
      typed(
        api.POST('/orgs/{orgId}/apps/{appId}/keys', {
          params: { path: { orgId, appId: input.appId } },
          body: { revoke_previous: input.revokePrevious },
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: appsQuery(orgId, projectId).queryKey }),
  })
}

export function useRevokeKey(orgId: string, projectId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: { appId: string; key: string }) =>
      noContent(
        api.DELETE('/orgs/{orgId}/apps/{appId}/keys/{key}', {
          params: { path: { orgId, appId: input.appId, key: input.key } },
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: appsQuery(orgId, projectId).queryKey }),
  })
}

export function useInvite(orgId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: { email: string; role: Role; locale?: 'en' | 'pt-BR' }) =>
      typed(api.POST('/orgs/{orgId}/invitations', { params: { path: { orgId } }, body: input })),
    onSuccess: () => qc.invalidateQueries({ queryKey: invitationsQuery(orgId).queryKey }),
  })
}

/** Accepts an invitation with the email's token (APS-274); /me then lists the organization. */
export function useAcceptInvitation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (token: string) => typed(api.POST('/invitations/accept', { body: { token } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: meQuery.queryKey }),
  })
}

/** Changes a member's role (APS-234); the API keeps the last owner and admins' limits. */
export function useChangeMemberRole(orgId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: Role }) =>
      typed(
        api.PATCH('/orgs/{orgId}/members/{userId}', {
          params: { path: { orgId, userId } },
          body: { role },
        }),
      ),
    onSuccess: (_, { userId }) =>
      Promise.all([
        qc.invalidateQueries({ queryKey: membersQuery(orgId).queryKey }),
        // Their own role: what the console offers them follows.
        qc.getQueryData(meQuery.queryKey)?.user.id === userId
          ? qc.invalidateQueries({ queryKey: meQuery.queryKey })
          : undefined,
      ]),
  })
}

/** Removes a member, or the caller leaves (APS-212): their agent grants there end too. */
export function useRemoveMember(orgId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (userId: string) =>
      noContent(
        api.DELETE('/orgs/{orgId}/members/{userId}', { params: { path: { orgId, userId } } }),
      ),
    onSuccess: (_, userId) =>
      Promise.all([
        qc.invalidateQueries({ queryKey: membersQuery(orgId).queryKey }),
        qc.invalidateQueries({ queryKey: agentGrantsQuery(orgId).queryKey }),
        // Leaving: the organization list comes again before the console moves on, so it never
        // goes back to the organization just left.
        qc.getQueryData(meQuery.queryKey)?.user.id === userId
          ? qc.invalidateQueries({ queryKey: meQuery.queryKey })
          : undefined,
      ]),
  })
}

export function useWithdrawInvitation(orgId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (invitationId: string) =>
      noContent(
        api.DELETE('/orgs/{orgId}/invitations/{invitationId}', {
          params: { path: { orgId, invitationId } },
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: invitationsQuery(orgId).queryKey }),
  })
}

// Billing (plan step 5.2)

/** The billing panel; undefined when billing is off (503), so the panel stays hidden. */
/** Sets or clears Pro's spend cap, in US cents (owners); answers with the period's usage. */
export function useSetSpendCap(orgId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (spendCapCents: number | null) =>
      typed(
        api.PUT('/orgs/{orgId}/spend-cap', {
          params: { path: { orgId } },
          body: { spend_cap_cents: spendCapCents },
        }),
      ),
    onSuccess: (usage) => qc.setQueryData(usageQuery(orgId).queryKey, usage),
  })
}

export const billingQuery = (orgId: string) =>
  queryOptions({
    queryKey: ['org', orgId, 'billing'],
    queryFn: async () => {
      try {
        return await typed(api.GET('/orgs/{orgId}/billing', { params: { path: { orgId } } }))
      } catch (error) {
        // Billing off on this server; an outage (503 unavailable) still shows as an error.
        if (notConfigured(error)) return null
        throw error
      }
    },
    staleTime: 60_000,
    retry: false,
  })

export const billingSetupIntent = (orgId: string) =>
  typed(api.POST('/orgs/{orgId}/billing/setup-intent', { params: { path: { orgId } } }))

export const billingPaymentMethod = (orgId: string, setupIntentId: string) =>
  typed(
    api.POST('/orgs/{orgId}/billing/payment-method', {
      params: { path: { orgId } },
      body: { setup_intent_id: setupIntentId },
    }),
  )

export function useSubscribe(orgId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: {
      plan: 'pro'
      interval: 'month' | 'year'
      setup_intent_id: string
      tax_id?: string
      address?: BrazilAddress
    }) =>
      typed(
        api.POST('/orgs/{orgId}/billing/subscribe', { params: { path: { orgId } }, body: input }),
      ),
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ['org', orgId] }),
        qc.invalidateQueries({ queryKey: ['me'] }),
      ]),
  })
}

/** Back from the billing portal: the API checks the card the owner may have changed. */
export const billingCheck = (orgId: string) =>
  typed(api.POST('/orgs/{orgId}/billing/check', { params: { path: { orgId } } }))

/** A SetupIntent on the organization's customer in reais, for the switch (D88 addendum). */
export const billingSwitchSetupIntent = (orgId: string) =>
  typed(api.POST('/orgs/{orgId}/billing/switch/setup-intent', { params: { path: { orgId } } }))

export function useSwitchCurrency(orgId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: { setup_intent_id: string; tax_id: string; address: BrazilAddress }) =>
      typed(api.POST('/orgs/{orgId}/billing/switch', { params: { path: { orgId } }, body: input })),
    onSuccess: () => qc.invalidateQueries({ queryKey: billingQuery(orgId).queryKey }),
  })
}

export const billingPortal = (orgId: string) =>
  typed(api.POST('/orgs/{orgId}/billing/portal', { params: { path: { orgId } } }))

export function useUpdateProfile() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: {
      name?: string
      locale?: 'en' | 'pt-BR'
      time_zone?: string | null
      analytics_consent?: 'granted' | 'denied'
    }) => typed(api.PATCH('/me', { body: input })),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me'] }),
  })
}

/** Deleting your own account (APS-402): whether it is scheduled, and what blocks or goes with it. */
export const ownErasureQuery = queryOptions({
  queryKey: ['me', 'erasure'],
  queryFn: () => typed(api.GET('/me/erasure')),
  retry: (count, error) => !isApiError(error, 503) && count < 2,
})

/** Schedules it (your email as the confirmation); the API signs you out. */
export const scheduleOwnErasure = (confirm: string) =>
  typed(api.POST('/me/erasure', { body: { confirm } }))

export const cancelOwnErasure = () => typed(api.DELETE('/me/erasure'))

/** Your signed-in CLIs (APS-486): each `apsio login` still signed in, the most recently used first. */
export const cliSessionsQuery = queryOptions({
  queryKey: ['me', 'cli-sessions'],
  queryFn: () => typed(api.GET('/me/cli-sessions')).then((r) => r.sessions),
})

/** Signs a CLI out: its tokens stop at once and its next command asks to sign in again. */
export function useSignOutCli() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (sessionId: string) =>
      noContent(api.DELETE('/me/cli-sessions/{sessionId}', { params: { path: { sessionId } } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: cliSessionsQuery.queryKey }),
  })
}

export async function signOut(): Promise<string | null> {
  const r = await typed(api.POST('/auth/sign-out'))
  return r.sign_out_url
}

export async function devSignIn(email: string, name: string, locale?: string): Promise<void> {
  // Local only: the route exists when the API runs with AUTH_PROVIDER=dev. It is not in the
  // OpenAPI document (D72), so it is called directly.
  const res = await fetch(`${import.meta.env.VITE_API_URL ?? ''}/v1/auth/dev/session`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ email, ...(name ? { name } : {}), ...(locale ? { locale } : {}) }),
  })
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: { message?: string } }
    throw new Error(body.error?.message ?? res.statusText)
  }
}

// Alerts (plan step 3.5): the organization's destinations, a project's rules and their history.

export const alertDestinationsQuery = (orgId: string) =>
  queryOptions({
    queryKey: ['org', orgId, 'alerts', 'destinations'],
    queryFn: () =>
      typed(api.GET('/orgs/{orgId}/alerts/destinations', { params: { path: { orgId } } })),
    retry: (count, error) => !isApiError(error, 503) && count < 2,
  })

export const alertRulesQuery = (orgId: string, projectId: string) =>
  queryOptions({
    queryKey: ['org', orgId, 'alerts', 'rules', projectId],
    queryFn: () =>
      typed(
        api.GET('/orgs/{orgId}/alerts/rules', {
          params: { path: { orgId }, query: { project_id: projectId } },
        }),
      ),
    retry: (count, error) => !isApiError(error, 503) && count < 2,
  })

/** A page of what alerting did, newest first: the project's, or one rule's (APS-303). */
export function fetchAlertEvents(
  orgId: string,
  q: { projectId: string; ruleId?: string | undefined; limit?: number },
  before?: string,
): Promise<AlertEventsPage> {
  return typed(
    api.GET('/orgs/{orgId}/alerts/events', {
      params: {
        path: { orgId },
        query: {
          project_id: q.projectId,
          limit: q.limit ?? 50,
          ...(q.ruleId ? { rule_id: q.ruleId } : {}),
          ...(before ? { before } : {}),
        },
      },
    }),
  )
}

export const alertEventsQuery = (orgId: string, projectId: string) =>
  queryOptions({
    queryKey: ['org', orgId, 'alerts', 'events', projectId],
    queryFn: () => fetchAlertEvents(orgId, { projectId }),
    retry: (count, error) => !isApiError(error, 503) && count < 2,
  })

/** The history as pages, for "Load more" (the API's `before` cursor). */
export const alertEventsInfiniteQuery = (orgId: string, projectId: string, ruleId?: string) =>
  infiniteQueryOptions({
    queryKey: ['org', orgId, 'alerts', 'events', projectId, 'pages', ruleId ?? ''],
    queryFn: ({ pageParam }) =>
      fetchAlertEvents(orgId, { projectId, ruleId, limit: 50 }, pageParam || undefined),
    initialPageParam: '',
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    retry: (count, error) => !isApiError(error, 503) && count < 2,
  })

/** One rule's events (at most 200, newest first): its firings, resolutions and messages. */
export const alertRuleEventsQuery = (orgId: string, projectId: string, ruleId: string) =>
  queryOptions({
    queryKey: ['org', orgId, 'alerts', 'events', projectId, 'rule', ruleId],
    queryFn: () => fetchAlertEvents(orgId, { projectId, ruleId, limit: 200 }),
    retry: (count, error) => !isApiError(error, 503) && count < 2,
  })

/** A rule's measure against its threshold (APS-303): 48 hours hourly, or 14 days daily. */
export const alertRuleSeriesQuery = (orgId: string, ruleId: string) =>
  queryOptions({
    queryKey: ['org', orgId, 'alerts', 'series', ruleId],
    queryFn: () =>
      typed(
        api.GET('/orgs/{orgId}/alerts/rules/{ruleId}/series', {
          params: { path: { orgId, ruleId } },
        }),
      ),
    retry: (count, error) => !isApiError(error, 503) && count < 2,
    staleTime: 60_000,
  })

const alertsKey = (orgId: string) => ['org', orgId, 'alerts']

/** Adds a destination; a webhook's signing secret is in the answer, this once. */
export function useCreateAlertDestination(orgId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (
      input:
        | { kind: 'slack' | 'webhook'; name: string; url: string }
        | { kind: 'pagerduty'; name: string; routing_key: string; region: 'us' | 'eu' }
        | { kind: 'email'; name: string; address: string },
    ) =>
      typed(
        api.POST('/orgs/{orgId}/alerts/destinations', {
          params: { path: { orgId } },
          body: input,
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: alertsKey(orgId) }),
  })
}

/**
 * A new address for an email destination (APS-477: one that bounced), which confirms again; a
 * destination named by its old address takes the new one as its name too.
 */
export function useEditAlertAddress(orgId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      destinationId,
      address,
      name,
    }: {
      destinationId: string
      address: string
      name?: string
    }) =>
      typed(
        api.PATCH('/orgs/{orgId}/alerts/destinations/{destinationId}', {
          params: { path: { orgId, destinationId } },
          body: { address, ...(name !== undefined ? { name } : {}) },
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: alertsKey(orgId) }),
  })
}

/** Replaces a PagerDuty destination's routing key; the key is never sent back. */
export function useReplaceRoutingKey(orgId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ destinationId, routingKey }: { destinationId: string; routingKey: string }) =>
      typed(
        api.PATCH('/orgs/{orgId}/alerts/destinations/{destinationId}', {
          params: { path: { orgId, destinationId } },
          body: { routing_key: routingKey },
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: alertsKey(orgId) }),
  })
}

export function useDeleteAlertDestination(orgId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (destinationId: string) =>
      noContent(
        api.DELETE('/orgs/{orgId}/alerts/destinations/{destinationId}', {
          params: { path: { orgId, destinationId } },
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: alertsKey(orgId) }),
  })
}

export function useTestAlertDestination(orgId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (destinationId: string) =>
      typed(
        api.POST('/orgs/{orgId}/alerts/destinations/{destinationId}/test', {
          params: { path: { orgId, destinationId } },
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: alertsKey(orgId) }),
  })
}

/** An email destination's confirmation, sent again (pending or unsubscribed; APS-477). */
export function useSendAlertConfirmation(orgId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (destinationId: string) =>
      typed(
        api.POST('/orgs/{orgId}/alerts/destinations/{destinationId}/send-confirmation', {
          params: { path: { orgId, destinationId } },
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: alertsKey(orgId) }),
  })
}

export function useRotateAlertSecret(orgId: string) {
  return useMutation({
    mutationFn: (destinationId: string) =>
      typed(
        api.POST('/orgs/{orgId}/alerts/destinations/{destinationId}/rotate-secret', {
          params: { path: { orgId, destinationId } },
        }),
      ),
  })
}

export interface AlertRuleInput {
  project_id: string
  app_id?: string | null
  preset: AlertPreset
  params: Record<string, number | string>
  destination_id: string
  cooldown_minutes?: number
}

export function useCreateAlertRule(orgId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: AlertRuleInput) =>
      typed(api.POST('/orgs/{orgId}/alerts/rules', { params: { path: { orgId } }, body: input })),
    onSuccess: () => qc.invalidateQueries({ queryKey: alertsKey(orgId) }),
  })
}

export function useUpdateAlertRule(orgId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      ruleId,
      ...patch
    }: {
      ruleId: string
      enabled?: boolean
      muted_until?: string | null
      destination_id?: string | null
    }) =>
      typed(
        api.PATCH('/orgs/{orgId}/alerts/rules/{ruleId}', {
          params: { path: { orgId, ruleId } },
          body: patch,
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: alertsKey(orgId) }),
  })
}

export function useDeleteAlertRule(orgId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (ruleId: string) =>
      noContent(
        api.DELETE('/orgs/{orgId}/alerts/rules/{ruleId}', {
          params: { path: { orgId, ruleId } },
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: alertsKey(orgId) }),
  })
}

/**
 * A run_query error as the API answers it (apps/api read/query.ts): its class, its message, and
 * for a syntax error the position in the SQL, or for a failed query ClickHouse's error class.
 */
export class QueryError extends ApiError {
  constructor(
    status: number,
    code: string,
    message: string,
    readonly position?: number,
    readonly clickhouseError?: string,
    /** Seconds to wait before trying again (a 429). */
    readonly retryAfter?: number,
  ) {
    super(status, code, message)
    this.name = 'QueryError'
  }
}

/** The tables run_query reads and their columns (the API's, not system.columns). */
export const querySchemaQuery = (projectId: string) =>
  queryOptions({
    queryKey: ['project', projectId, 'query-schema'],
    queryFn: async (): Promise<QuerySchema> =>
      unwrap(
        await api.GET('/projects/{projectId}/query/schema', { params: { path: { projectId } } }),
      ),
    staleTime: 3_600_000,
  })

/** Runs one read-only SQL query (plan step 3.5b). Not cached: each run is its own. */
export function useRunQuery(projectId: string) {
  return useMutation({
    mutationFn: async (sql: string): Promise<QueryResult> => {
      const result = await api.POST('/projects/{projectId}/query', {
        params: { path: { projectId } },
        body: { sql },
      })
      if (result.response.ok && result.data) return result.data
      throw queryError(result)
    },
  })
}

// Agent handoff (step 4.3): GitHub and Linear destinations that open issues for a coding agent.

/** Starts connecting GitHub or Linear: where to send the owner. */
export function useConnectHandoff(orgId: string) {
  return useMutation({
    mutationFn: (input: { provider: 'github' | 'linear'; project_id: string }) =>
      typed(
        api.POST('/orgs/{orgId}/handoff/connect', { params: { path: { orgId } }, body: input }),
      ),
  })
}

export const handoffConnectionQuery = (orgId: string, connectionId: string) =>
  queryOptions({
    queryKey: ['org', orgId, 'handoff', 'connection', connectionId],
    queryFn: () =>
      typed(
        api.GET('/orgs/{orgId}/handoff/connections/{connectionId}', {
          params: { path: { orgId, connectionId } },
        }),
      ),
    retry: false,
  })

export function useCreateHandoffDestination(orgId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: {
      connection_id: string
      name: string
      repository_id?: number
      team_id?: string
      daily_cap?: number
    }) =>
      typed(
        api.POST('/orgs/{orgId}/handoff/destinations', {
          params: { path: { orgId } },
          body: input,
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: alertsKey(orgId) }),
  })
}

/** Turns a handoff destination on (owners, typing its host to confirm) or off. */
export function useSetHandoff(orgId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      destinationId,
      ...body
    }: {
      destinationId: string
      enabled: boolean
      confirm?: string
    }) =>
      typed(
        api.POST('/orgs/{orgId}/alerts/destinations/{destinationId}/handoff', {
          params: { path: { orgId, destinationId } },
          body,
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: alertsKey(orgId) }),
  })
}

/**
 * A project's handoffs, newest first (at most 200), and `total`, every one under the same filter;
 * `since` keeps those created from then on (the Agents page's window, APS-389).
 */
export const handoffsQuery = (orgId: string, projectId: string, since?: string) =>
  queryOptions({
    queryKey: ['org', orgId, 'alerts', 'handoffs', projectId, ...(since ? [since] : [])],
    queryFn: () =>
      typed(
        api.GET('/orgs/{orgId}/handoffs', {
          params: {
            path: { orgId },
            query: { project_id: projectId, ...(since ? { since } : {}) },
          },
        }),
      ),
    retry: (count, error) => !isApiError(error, 503) && count < 2,
  })

/** An issue's handoffs, newest first, each with the pull request that references it (APS-390). */
export const issueHandoffsQuery = (projectId: string, issueId: string) =>
  queryOptions({
    queryKey: ['project', projectId, 'issue', issueId, 'handoffs'],
    queryFn: async () =>
      unwrap(
        await api.GET('/projects/{projectId}/issues/{issueId}/handoffs', {
          params: { path: { projectId, issueId } },
        }),
      ),
    retry: (count, error) => !isApiError(error, 503) && count < 2,
  })

/** Hands an issue to the org's coding agent through a handoff destination (D103 #13). */
export function useIssueHandoff(orgId: string, projectId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ issueId, destinationId }: { issueId: string; destinationId: string }) =>
      unwrap(
        await api.POST('/projects/{projectId}/issues/{issueId}/handoff', {
          params: { path: { projectId, issueId } },
          body: { destination_id: destinationId },
        }),
      ),
    onSuccess: (handoff: IssueHandoff, { issueId }) => {
      // The issue page's rail shows the new handoff at once, then reads the list again.
      const key = issueHandoffsQuery(projectId, issueId).queryKey
      qc.setQueryData(key, (old) => ({
        handoffs: [handoff, ...(old?.handoffs ?? []).filter((h) => h.id !== handoff.id)],
      }))
      qc.invalidateQueries({ queryKey: key })
      qc.invalidateQueries({ queryKey: ['org', orgId, 'alerts', 'handoffs', projectId] })
    },
  })
}

/** A run_query error answer as a QueryError, with when to try again for a 429. */
function queryError(result: { error?: unknown; response: Response }): QueryError {
  const error = (result.error as { error?: Record<string, unknown> } | undefined)?.error ?? {}
  const wait = Number(result.response.headers.get('Retry-After'))
  return new QueryError(
    result.response.status,
    typeof error.code === 'string' ? error.code : 'unknown',
    typeof error.message === 'string' ? error.message : result.response.statusText,
    typeof error.position === 'number' ? error.position : undefined,
    typeof error.clickhouse_error === 'string' ? error.clickhouse_error : undefined,
    // The API sends it, but a browser only shows it to another origin when exposed: else 60 s.
    result.response.status === 429 ? (Number.isFinite(wait) && wait > 0 ? wait : 60) : undefined,
  )
}

// Dashboards (plan step 3.5, D98): a project's boards of SQL and preset panels. Members create
// and edit, owners and admins delete. A panel's run is not cached here: the board decides when
// to run (lib/panels.ts), and the API keeps a result for a minute.

const dashboardsKey = (projectId: string) => ['project', projectId, 'dashboards']

export const dashboardsQuery = (projectId: string) =>
  queryOptions({
    queryKey: dashboardsKey(projectId),
    queryFn: async (): Promise<Dashboard[]> =>
      unwrap(await api.GET('/projects/{projectId}/dashboards', { params: { path: { projectId } } }))
        .dashboards,
    retry: (count, error) => !isApiError(error, 503) && !isApiError(error, 403) && count < 2,
  })

export const dashboardQuery = (projectId: string, dashboardId: string) =>
  queryOptions({
    queryKey: [...dashboardsKey(projectId), dashboardId],
    queryFn: async (): Promise<{ dashboard: Dashboard; panels: DashboardPanel[] }> =>
      unwrap(
        await api.GET('/projects/{projectId}/dashboards/{dashboardId}', {
          params: { path: { projectId, dashboardId } },
        }),
      ),
    retry: (count, error) => !isApiError(error, 404) && !isApiError(error, 403) && count < 2,
  })

export function useCreateDashboard(projectId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (name: string): Promise<Dashboard> =>
      unwrap(
        await api.POST('/projects/{projectId}/dashboards', {
          params: { path: { projectId } },
          body: { name },
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: dashboardsKey(projectId) }),
  })
}

export function useRenameDashboard(projectId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ dashboardId, name }: { dashboardId: string; name: string }) =>
      unwrap(
        await api.PATCH('/projects/{projectId}/dashboards/{dashboardId}', {
          params: { path: { projectId, dashboardId } },
          body: { name },
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: dashboardsKey(projectId) }),
  })
}

export function useDeleteDashboard(projectId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (dashboardId: string) =>
      noContent(
        api.DELETE('/projects/{projectId}/dashboards/{dashboardId}', {
          params: { path: { projectId, dashboardId } },
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: dashboardsKey(projectId) }),
  })
}

export function useAddPanel(projectId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ dashboardId, panel }: { dashboardId: string; panel: PanelInput }) =>
      unwrap(
        await api.POST('/projects/{projectId}/dashboards/{dashboardId}/panels', {
          params: { path: { projectId, dashboardId } },
          body: panel,
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: dashboardsKey(projectId) }),
  })
}

export function useUpdatePanel(projectId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      dashboardId,
      panelId,
      patch,
    }: {
      dashboardId: string
      panelId: string
      patch: PanelPatch
    }) =>
      unwrap(
        await api.PATCH('/projects/{projectId}/dashboards/{dashboardId}/panels/{panelId}', {
          params: { path: { projectId, dashboardId, panelId } },
          body: patch,
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: dashboardsKey(projectId) }),
  })
}

export function useDeletePanel(projectId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ dashboardId, panelId }: { dashboardId: string; panelId: string }) =>
      noContent(
        api.DELETE('/projects/{projectId}/dashboards/{dashboardId}/panels/{panelId}', {
          params: { path: { projectId, dashboardId, panelId } },
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: dashboardsKey(projectId) }),
  })
}

/**
 * Runs one panel over a range: a SQL panel's rows (as POST /query answers) or a preset's answer.
 * Errors are QueryErrors, as Explore's, with `retryAfter` on a 429.
 */
export async function runPanel(
  projectId: string,
  dashboardId: string,
  panelId: string,
  range: { from: string; to: string },
  signal?: AbortSignal,
): Promise<QueryResult | PresetPanelResult> {
  const result = await api.POST(
    '/projects/{projectId}/dashboards/{dashboardId}/panels/{panelId}/run',
    {
      params: { path: { projectId, dashboardId, panelId } },
      body: { range },
      ...(signal ? { signal } : {}),
    },
  )
  if (result.response.ok && result.data) return result.data
  throw queryError(result)
}

/** Agent access (D98): the grants that let a user's OAuth clients write dashboards here. */
export const agentGrantsQuery = (orgId: string) =>
  queryOptions({
    queryKey: ['org', orgId, 'agent-grants'],
    queryFn: () =>
      typed(api.GET('/orgs/{orgId}/agent-grants', { params: { path: { orgId } } })).then(
        (r) => r.grants,
      ),
  })

export function useCreateAgentGrant(orgId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: {
      client_id: string
      client_name: string
      capability: 'dashboards:write' | 'issues:write' | 'instrumentation:write' | 'exports:write'
    }) =>
      typed(api.POST('/orgs/{orgId}/agent-grants', { params: { path: { orgId } }, body: input })),
    onSuccess: () => qc.invalidateQueries({ queryKey: agentGrantsQuery(orgId).queryKey }),
  })
}

export function useRevokeAgentGrant(orgId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (grantId: string) =>
      noContent(
        api.DELETE('/orgs/{orgId}/agent-grants/{grantId}', {
          params: { path: { orgId, grantId } },
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: agentGrantsQuery(orgId).queryKey }),
  })
}

export function useRevokeAllAgentGrants(orgId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (everyone: boolean) =>
      typed(
        api.POST('/orgs/{orgId}/agent-grants/revoke-all', {
          params: { path: { orgId } },
          body: { everyone },
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: agentGrantsQuery(orgId).queryKey }),
  })
}

// Project tokens (D101): the project's tokens (never a token), and a new one shown once.

export const projectTokensQuery = (orgId: string, projectId: string) =>
  queryOptions({
    queryKey: ['org', orgId, 'project', projectId, 'tokens'],
    queryFn: () =>
      typed(
        api.GET('/orgs/{orgId}/projects/{projectId}/tokens', {
          params: { path: { orgId, projectId } },
        }),
      ).then((r) => r.tokens),
  })

export function useCreateProjectToken(orgId: string, projectId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: {
      name: string
      scopes: ('dashboards:write' | 'issues:write')[]
      days: 7 | 30 | 90 | 365
    }) =>
      typed(
        api.POST('/orgs/{orgId}/projects/{projectId}/tokens', {
          params: { path: { orgId, projectId } },
          body: input,
        }),
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: projectTokensQuery(orgId, projectId).queryKey }),
  })
}

export function useRevokeProjectToken(orgId: string, projectId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (tokenId: string) =>
      noContent(
        api.DELETE('/orgs/{orgId}/projects/{projectId}/tokens/{tokenId}', {
          params: { path: { orgId, projectId, tokenId } },
        }),
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: projectTokensQuery(orgId, projectId).queryKey }),
  })
}

/** The project's symbol upload tokens that still work (D91 as amended, APS-456). */
export const uploadTokensQuery = (orgId: string, projectId: string) =>
  queryOptions({
    queryKey: ['org', orgId, 'project', projectId, 'upload-tokens'],
    queryFn: () =>
      typed(
        api.GET('/orgs/{orgId}/projects/{projectId}/upload-tokens', {
          params: { path: { orgId, projectId } },
        }),
      ).then((r) => r.upload_tokens),
  })

export function useCreateUploadToken(orgId: string, projectId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: { name: string; days: 7 | 30 | 90 | 365 }) =>
      typed(
        api.POST('/orgs/{orgId}/projects/{projectId}/upload-tokens', {
          params: { path: { orgId, projectId } },
          body: input,
        }),
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: uploadTokensQuery(orgId, projectId).queryKey }),
  })
}

export function useRevokeUploadToken(orgId: string, projectId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (tokenId: string) =>
      noContent(
        api.DELETE('/orgs/{orgId}/projects/{projectId}/upload-tokens/{tokenId}', {
          params: { path: { orgId, projectId, tokenId } },
        }),
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: uploadTokensQuery(orgId, projectId).queryKey }),
  })
}

// Explore's builders (APS-198): the API builds the SQL from the structured query.

/** One page of Explore › Logs; the first page carries the volume. */
export async function fetchExploreLogs(
  projectId: string,
  body: ExploreLogsQuery,
  cursor?: string,
): Promise<ExploreLogs> {
  return unwrap(
    await api.POST('/projects/{projectId}/explore/logs', {
      params: { path: { projectId } },
      body: { ...body, ...(cursor ? { cursor } : {}) },
    }),
  )
}

/** The Logs page's break down (APS-327): each facet counted without its own filter. */
export async function fetchExploreLogFacets(
  projectId: string,
  body: ExploreLogFacetsQuery,
): Promise<ExploreLogFacets> {
  return unwrap(
    await api.POST('/projects/{projectId}/explore/logs/facets', {
      params: { path: { projectId } },
      body,
    }),
  )
}

/** Explore › Traces: one page of spans, and the volume and durations on the first. */
export async function fetchExploreTraces(
  projectId: string,
  body: ExploreTracesQuery,
  cursor?: string,
): Promise<ExploreTraces> {
  return unwrap(
    await api.POST('/projects/{projectId}/explore/traces', {
      params: { path: { projectId } },
      body: { ...body, ...(cursor ? { cursor } : {}) },
    }),
  )
}

/** Attribute keys and their top values seen in the last day, for the query bar's suggestions. */
export const exploreFieldsQuery = (
  projectId: string,
  appId?: string,
  signal: 'logs' | 'spans' = 'logs',
) =>
  queryOptions({
    queryKey: ['project', projectId, 'explore-fields', signal, appId ?? 'all'],
    queryFn: async (): Promise<ExploreFields> =>
      unwrap(
        await api.GET('/projects/{projectId}/explore/fields', {
          params: {
            path: { projectId },
            query: { signal, ...(appId ? { app_id: appId } : {}) },
          },
        }),
      ),
    staleTime: 5 * 60_000,
  })

/** Explore › Metrics: one metric per bucket, optionally split by a field. */
export const exploreMetricsQuery = (projectId: string, body: ExploreMetricsQuery | undefined) =>
  queryOptions({
    queryKey: ['project', projectId, 'explore-metrics', body],
    queryFn: async (): Promise<ExploreMetrics> =>
      unwrap(
        await api.POST('/projects/{projectId}/explore/metrics', {
          params: { path: { projectId } },
          body: body as ExploreMetricsQuery,
        }),
      ),
    enabled: !!body,
    retry: false,
  })

/** The metrics of the last week, with their kinds and attribute keys, for the pickers. */
export const exploreMetricsCatalogQuery = (projectId: string, appId?: string) =>
  queryOptions({
    queryKey: ['project', projectId, 'explore-metrics-catalog', appId ?? 'all'],
    queryFn: async (): Promise<ExploreMetricsCatalog> =>
      unwrap(
        await api.GET('/projects/{projectId}/explore/metrics/catalog', {
          params: { path: { projectId }, query: appId ? { app_id: appId } : {} },
        }),
      ),
    staleTime: 5 * 60_000,
  })

/** The project's OTLP exports (D115), newest first: owners and admins (403 for members). */
export const exportsQuery = (orgId: string, projectId: string) =>
  queryOptions({
    queryKey: ['org', orgId, 'project', projectId, 'exports'],
    queryFn: async () =>
      (
        await typed(
          api.GET('/orgs/{orgId}/projects/{projectId}/exports', {
            params: { path: { orgId, projectId } },
          }),
        )
      ).exports,
    retry: (count, error) => !isApiError(error, 403) && count < 2,
    // Queued and running exports move on their own: look again while one is under way.
    refetchInterval: (q) =>
      q.state.data?.some((e) => e.status === 'queued' || e.status === 'running') ? 10_000 : false,
  })

/** Queues an export of the project's signals over [from, to). */
export function useCreateExport(orgId: string, projectId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: { signals: ExportSignal[]; from: string; to: string }) =>
      typed(
        api.POST('/orgs/{orgId}/projects/{projectId}/exports', {
          params: { path: { orgId, projectId } },
          body: input,
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: exportsQuery(orgId, projectId).queryKey }),
  })
}

/** A done export's files with fresh 15-minute links; asked again each time the list opens. */
export const exportFilesQuery = (orgId: string, projectId: string, exportId: string) =>
  queryOptions({
    queryKey: ['org', orgId, 'project', projectId, 'exports', exportId, 'files'],
    queryFn: () =>
      typed(
        api.GET('/orgs/{orgId}/projects/{projectId}/exports/{exportId}/files', {
          params: { path: { orgId, projectId, exportId } },
        }),
      ),
    // Links expire: never kept for another opening.
    staleTime: 0,
    gcTime: 0,
    retry: (count, error) => !isApiError(error, 410) && !isApiError(error, 403) && count < 2,
  })
